import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { supabase } from '@/lib/supabase';
import { ALL_CREDIT_REASON_TYPES } from '@/components/customers/credits/CreateManualCreditModal';
import UniversalStatusBadge from '@/components/shared/UniversalStatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { FileText, Eye, Printer, Plus } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import CreditNotePreviewModal from '@/components/credits/CreditNotePreviewModal';
import CreditNoteCard from '@/components/credits/CreditNoteCard';
import EntityActions from '@/components/shared/EntityActions';
import PageLayout from '@/components/layout/PageLayout';
import PageHeader from '@/components/layout/PageHeader';
import ViewToggle from '@/components/shared/ViewToggle';
import DataTableWrapper from '@/components/shared/DataTableWrapper';
import FilterToolbar from '@/components/shared/FilterToolbar';
import { FilterBuilder } from '@/components/invoices/FilterBuilder';
import { useViewPreference } from '@/hooks/useViewPreference';
import { useCreditNoteFilterBuilder } from './hooks/useCreditNoteFilterBuilder';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface CreditWithCustomer {
  id: string;
  credit_amount: number;
  remaining_amount: number;
  source_type: string;
  source_invoice_id: string | null;
  description: string | null;
  status: string;
  credit_note_number: string | null;
  credit_note_issued_at: string | null;
  reason_type: string | null;
  created_at: string;
  customer: {
    id: string;
    name: string;
    last_name: string;
    city: string | null;
    province_state: string | null;
    suburb: string | null;
    tags: string[] | null;
    postal_code: string | null;
    customer_type: string;
    lead_source: string | null;
    emails: string[];
    cell_numbers: string[];
  } | null;
  source_invoice: {
    invoice_number: string | null;
  } | null;
}

const SORT_OPTIONS = [
  { value: 'created_at', label: 'Date Created' },
  { value: 'credit_note_number', label: 'Credit Note #' },
  { value: 'credit_amount', label: 'Amount' },
  { value: 'remaining_amount', label: 'Remaining' },
  { value: 'customer_name', label: 'Customer Name' },
  { value: 'status', label: 'Status' },
];

const CreditNotes: React.FC = () => {
  const { profile } = useAuth();
  const companyId = profile?.company_id;
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [previewCreditId, setPreviewCreditId] = useState<string | null>(null);
  const { viewMode, setViewMode } = useViewPreference({ entityType: 'credits', defaultView: 'list' });

  const { data: credits, isLoading } = useQuery({
    queryKey: ['company-credits', companyId],
    queryFn: async () => {
      if (!companyId) return [];

      const { data, error } = await supabase
        .from('customer_credits')
        .select(`
          *,
          customer:customers!customer_credits_customer_id_fkey(id, name, last_name, city, province_state, suburb, postal_code, tags, customer_type, lead_source, emails, cell_numbers),
          source_invoice:invoices!customer_credits_source_invoice_id_fkey(invoice_number)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as CreditWithCustomer[];
    },
    enabled: !!companyId,
  });

  const {
    draftGroups,
    appliedGroups,
    filteredCredits: advancedFilteredCredits,
    hasActiveFilters,
    hasUnappliedChanges,
    canApplyFilters,
    addGroup,
    updateGroup,
    removeGroup,
    addRuleToGroup,
    updateRuleInGroup,
    removeRuleFromGroup,
    clearAllRules,
    applyFilters,
    filterFields,
    filterEntities,
    filterAttributesByEntity,
  } = useCreditNoteFilterBuilder(credits || []);

  const renderStatusBadge = (status: string) => (
    <UniversalStatusBadge entityType="credit" status={status} size="sm" />
  );

  const sortedAndFilteredCredits = useMemo(() => {
    // Apply search on top of advanced-filtered results
    let result = advancedFilteredCredits.filter((credit) => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        credit.credit_note_number?.toLowerCase().includes(searchLower) ||
        credit.customer?.name?.toLowerCase().includes(searchLower) ||
        credit.customer?.last_name?.toLowerCase().includes(searchLower) ||
        credit.source_invoice?.invoice_number?.toLowerCase().includes(searchLower)
      );
    });

    result.sort((a, b) => {
      let cmp = 0;
      switch (sortBy) {
        case 'created_at':
          cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case 'credit_note_number':
          cmp = (a.credit_note_number || '').localeCompare(b.credit_note_number || '');
          break;
        case 'credit_amount':
          cmp = a.credit_amount - b.credit_amount;
          break;
        case 'remaining_amount':
          cmp = a.remaining_amount - b.remaining_amount;
          break;
        case 'customer_name': {
          const nameA = a.customer ? `${a.customer.name} ${a.customer.last_name}` : '';
          const nameB = b.customer ? `${b.customer.name} ${b.customer.last_name}` : '';
          cmp = nameA.localeCompare(nameB);
          break;
        }
        case 'status':
          cmp = a.status.localeCompare(b.status);
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [advancedFilteredCredits, searchTerm, sortBy, sortDir]);

  const getCreditActions = (credit: CreditWithCustomer) => [
    {
      label: "View Details",
      icon: Eye,
      onClick: () => navigate(`/credits/${credit.id}`),
    },
    {
      label: "View/Print Credit Note",
      icon: Printer,
      onClick: () => setPreviewCreditId(credit.id),
      hidden: !credit.credit_note_number,
    },
  ];

  return (
    <PageLayout>
      <PageHeader
        title="Customer Credits"
        description="Manage customer credits and print credit notes"
      />

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Customer Credits ({sortedAndFilteredCredits.length})
            </CardTitle>
            <ViewToggle value={viewMode} onChange={setViewMode} />
          </div>

          <FilterToolbar
            searchValue={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Search by customer, credit note #, invoice #..."
            sortOptions={SORT_OPTIONS}
            sortValue={sortBy}
            onSortChange={setSortBy}
            sortDir={sortDir}
            onSortDirChange={setSortDir}
            actions={
              <Button onClick={() => navigate('/finances/credit-notes/create')} size="sm" className="gap-1.5">
                <Plus className="h-4 w-4 shrink-0" />
                <span className="hidden sm:inline truncate">Add Credit</span>
                <span className="sm:hidden truncate">New</span>
              </Button>
            }
            advancedFilter={
              <FilterBuilder
                draftGroups={draftGroups}
                appliedGroups={appliedGroups}
                entities={filterEntities}
                attributesByEntity={filterAttributesByEntity}
                fields={filterFields}
                onAddGroup={addGroup}
                onUpdateGroup={updateGroup}
                onRemoveGroup={removeGroup}
                onAddRuleToGroup={addRuleToGroup}
                onUpdateRuleInGroup={updateRuleInGroup}
                onRemoveRuleFromGroup={removeRuleFromGroup}
                onClearAll={clearAllRules}
                onApplyFilters={applyFilters}
                hasActiveFilters={hasActiveFilters}
                hasUnappliedChanges={hasUnappliedChanges}
                canApplyFilters={canApplyFilters}
              />
            }
            hasAdvancedFilters={hasActiveFilters}
            advancedFilterCount={appliedGroups.reduce((c, g) => c + g.rules.filter(r => r.entity && r.attribute && r.value).length, 0)}
            onClearAdvancedFilters={clearAllRules}
          />
        </CardHeader>
        <CardContent className={viewMode === 'list' ? 'p-0' : undefined}>
          <DataTableWrapper
            isLoading={isLoading}
            isEmpty={sortedAndFilteredCredits.length === 0}
            emptyState={{
              title: "No credits found",
              description: hasActiveFilters || searchTerm
                ? "No credits match your current filters."
                : "Customer credits will appear here when created from overpayments or manual entries.",
              icon: <FileText className="h-12 w-12 text-muted-foreground" />,
            }}
            skeletonColumns={8}
          >
            {viewMode === 'list' ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap min-w-[10rem]">Credit Note #</TableHead>
                    <TableHead className="hidden sm:table-cell whitespace-nowrap">Customer</TableHead>
                    <TableHead className="text-right whitespace-nowrap">Amount</TableHead>
                    <TableHead className="hidden lg:table-cell text-right whitespace-nowrap">Remaining</TableHead>
                    <TableHead className="hidden md:table-cell whitespace-nowrap">Source</TableHead>
                    <TableHead className="hidden lg:table-cell whitespace-nowrap">Reason</TableHead>
                    <TableHead className="whitespace-nowrap">Status</TableHead>
                    <TableHead className="hidden lg:table-cell whitespace-nowrap">Created</TableHead>
                    <TableHead className="text-right w-12">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedAndFilteredCredits.map((credit) => (
                    <TableRow 
                      key={credit.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/credits/${credit.id}`)}
                    >
                      <TableCell className="max-w-0 w-full">
                        <div className="min-w-0">
                          {credit.credit_note_number ? (
                            <span className="font-mono font-medium">
                              {credit.credit_note_number}
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-sm">
                              Not issued
                            </span>
                          )}
                          {credit.customer && (
                            <div className="sm:hidden text-xs text-muted-foreground mt-0.5">
                              {credit.customer.name} {credit.customer.last_name}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        {credit.customer ? (
                          <Link
                            to={`/customers?selected=${credit.customer.id}`}
                            className="text-primary hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {credit.customer.name} {credit.customer.last_name}
                          </Link>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium whitespace-nowrap font-mono">
                        {formatCurrency(credit.credit_amount)}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-right whitespace-nowrap font-mono">
                        {formatCurrency(credit.remaining_amount)}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {credit.source_type === 'overpayment' && credit.source_invoice?.invoice_number
                          ? `Overpayment - ${credit.source_invoice.invoice_number}`
                          : credit.source_type}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {credit.reason_type ? (
                          ALL_CREDIT_REASON_TYPES.find(t => t.value === credit.reason_type)?.label || credit.reason_type
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>{renderStatusBadge(credit.status)}</TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {format(new Date(credit.created_at), 'dd MMM yyyy')}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <EntityActions actions={getCreditActions(credit)} size="sm" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <CreditNoteCard credits={sortedAndFilteredCredits} onPreview={setPreviewCreditId} />
            )}
          </DataTableWrapper>
        </CardContent>
      </Card>

      <CreditNotePreviewModal
        creditId={previewCreditId || ''}
        isOpen={!!previewCreditId}
        onOpenChange={(open) => !open && setPreviewCreditId(null)}
      />
    </PageLayout>
  );
};

export default CreditNotes;
